import { createContext, useContext, useState, useEffect, useRef, ReactNode } from "react";

interface KonamiContextType {
  konamiMode: boolean;
  konamiToggleCount: number;
  konamiEffectTrigger: number;
  activateKonami: () => void;
  setKonamiModeDirect: (value: boolean) => void;
}

const KonamiContext = createContext<KonamiContextType | undefined>(undefined);

export const KonamiProvider = ({ children }: { children: ReactNode }) => {
  const [konamiMode, setKonamiMode] = useState(false);
  const [konamiToggleCount, setKonamiToggleCount] = useState(0);
  const [konamiEffectTrigger, setKonamiEffectTrigger] = useState(0);
  const toggleCountRef = useRef(0);

  useEffect(() => {
    const persisted = sessionStorage.getItem("konamiMode") === "true";
    const count = parseInt(sessionStorage.getItem("konamiToggleCount") || "0", 10);
    if (persisted) setKonamiMode(true);
    if (count > 0) {
      setKonamiToggleCount(count);
      toggleCountRef.current = count;
    }
  }, []);

  // Apply konami-mode class to document body when theme changes
  useEffect(() => {
    if (konamiMode) {
      document.body.classList.add("konami-mode");
      console.log("🎮 Konami mode activated");
    } else {
      document.body.classList.remove("konami-mode");
      console.log("🔄 Matrix mode activated");
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove("konami-mode");
    };
  }, [konamiMode]);

  useEffect(() => {
    let index = 0;
    const sequence = [
      "ArrowUp",
      "ArrowUp",
      "ArrowDown",
      "ArrowDown",
      "ArrowLeft",
      "ArrowRight",
      "ArrowLeft",
      "ArrowRight",
      "b",
      "a",
    ];

    const handler = (e: KeyboardEvent) => {
      if (e.key === sequence[index]) {
        index++;
        if (index === sequence.length) {
          triggerKonami();
          index = 0;
        }
      } else {
        index = 0;
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const triggerKonami = () => {
    const next = toggleCountRef.current + 1;
    toggleCountRef.current = next;
    setKonamiToggleCount(next);
    sessionStorage.setItem("konamiToggleCount", next.toString());

    if (next > 2) {
      setKonamiMode(false);
      sessionStorage.removeItem("konamiMode");
    } else {
      setKonamiMode(true);
      sessionStorage.setItem("konamiMode", "true");
    }

    setKonamiEffectTrigger(prev => prev + 1);
  };

  const activateKonami = () => {
    triggerKonami();
  };

  return (
    <KonamiContext.Provider
      value={{
        konamiMode,
        konamiToggleCount,
        konamiEffectTrigger,
        activateKonami,
        setKonamiModeDirect: (value: boolean) => {
          setKonamiMode(value);
          if (value) {
            sessionStorage.setItem("konamiMode", "true");
          } else {
            sessionStorage.removeItem("konamiMode");
          }
        },
      }}
    >
      {children}
    </KonamiContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useKonami = () => {
  const context = useContext(KonamiContext);
  if (!context) throw new Error("useKonami must be used within a KonamiProvider");
  return context;
};
